import { MicrostoreLocationTemplateFormValues } from '../FormHelper/DataMapper';
import { ProductArrangementReassortmentType } from 'data/__generated__';

export type Badge = {
    icon: string;
    tooltip: string;
    color?: string;
};

export type LocationStatus = 'empty' | 'configured' | 'incomplete' | 'error';

/**
 * Détermine le statut d'une location
 */
export const getLocationStatus = (location: MicrostoreLocationTemplateFormValues): LocationStatus => {
    const hasProductArrangement = !!location.productArrangement;
    const hasProduct = !!location.product;
    const reassortmentType = location.reassortmentType;

    // Vide si ni arrangement ni produit
    if (!hasProductArrangement && !hasProduct) {
        return 'empty';
    }

    // Si produit sans arrangement, vérifier les champs requis selon le type de réassort
    if (hasProduct && !hasProductArrangement) {
        const isStockReassortmentType =
            reassortmentType === ProductArrangementReassortmentType.Manual ||
            reassortmentType === ProductArrangementReassortmentType.Target;

        const missingFields = [
            !reassortmentType,
            isStockReassortmentType && location.stockMax === null,
            reassortmentType === ProductArrangementReassortmentType.Tray &&
                (!location.trayMaxCapacity || location.trayMaxCapacity <= 0),
        ].filter(Boolean);

        if (missingFields.length > 0) {
            return 'incomplete';
        }
    }

    return 'configured';
};

/**
 * Génère les badges pour une location donnée
 */
export const getLocationBadges = (location: MicrostoreLocationTemplateFormValues): Badge[] => {
    const badges: Badge[] = [];
    const status = getLocationStatus(location);

    // Ne pas afficher de badges pour les emplacements vides
    if (status === 'empty') {
        return [];
    }

    // Badge de statut incomplet/erreur
    if (status === 'incomplete') {
        badges.push({ icon: '⚠️', tooltip: 'Configuration incomplète', color: '#FF9800' });
        return badges; // On retourne seulement le warning si incomplet
    }

    if (status === 'error') {
        badges.push({ icon: '🔴', tooltip: 'Erreur de validation', color: '#F44336' });
        return badges;
    }

    // Type de réassort
    const reassortmentType = location.productArrangement?.reassortmentType || location.reassortmentType;
    if (reassortmentType === ProductArrangementReassortmentType.Target) {
        badges.push({ icon: '🎯', tooltip: 'Stock cible (automatique)', color: '#4CAF50' });
    } else if (reassortmentType === ProductArrangementReassortmentType.Manual) {
        badges.push({ icon: '✋', tooltip: 'Réassort manuel (menus)', color: '#2196F3' });
    } else if (reassortmentType === ProductArrangementReassortmentType.Tray) {
        badges.push({ icon: '🛢', tooltip: 'Réassort bac (recette machine à café)', color: '#FF9800' });
    } else if (reassortmentType === ProductArrangementReassortmentType.HotDrink) {
        badges.push({ icon: '☕', tooltip: 'Réassort machine à café', color: '#795548' });
    }

    // Rotation
    if (location.allowProductRotation) {
        badges.push({ icon: '🔄', tooltip: 'Rotation activée', color: '#9C27B0' });
    }

    // Transitions
    if (location.shouldSmoothProductTransition) {
        badges.push({ icon: '⏳', tooltip: 'Attendre épuisement du produit actuel', color: '#FF9800' });
    }

    // Commande en avance
    if (location.shouldOrderProductIfNotInSite) {
        badges.push({ icon: '⚡', tooltip: 'Commander en avance', color: '#FFEB3B' });
    }

    return badges;
};

/**
 * Retourne la couleur de fond selon le statut
 */
export const getLocationBackgroundColor = (status: LocationStatus): string => {
    switch (status) {
        case 'empty':
            return '#FAFAFA';
        case 'configured':
            return '#FFFFFF';
        case 'incomplete':
            return '#FFF3E0';
        case 'error':
            return '#FFEBEE';
        default:
            return '#FFFFFF';
    }
};

/**
 * Retourne la couleur de bordure selon le statut et si sélectionné
 */
export const getLocationBorderColor = (status: LocationStatus, isSelected: boolean): string => {
    if (isSelected) {
        return '#2196F3';
    }

    switch (status) {
        case 'empty':
            return '#E0E0E0';
        case 'configured':
            return '#E0E0E0';
        case 'incomplete':
            return '#FF9800';
        case 'error':
            return '#F44336';
        default:
            return '#E0E0E0';
    }
};

/**
 * Retourne le nom à afficher pour la location
 */
export const getLocationDisplayName = (location: MicrostoreLocationTemplateFormValues): string => {
    if (location.productArrangement) {
        return location.productArrangement.name;
    }
    if (location.product) {
        return `${location.product.name} - ${location.product.brand}`;
    }
    return '';
};

/**
 * Retourne le nombre de produits dans la location
 */
export const getLocationProductCount = (location: MicrostoreLocationTemplateFormValues): string => {
    if (location.productArrangement) {
        const count = location.productArrangement.productsWithStockMax.length;
        return `${count} produit${count > 1 ? 's' : ''}`;
    }
    if (location.product) {
        return 'Produit seul';
    }
    return '';
};
